/**
 * The v2 evaluator: pure `(blocks, frame) → CSS delta`. Preview, editor
 * scrubbing, and the Lambda render all sample this same function, so the
 * three contexts cannot drift.
 *
 * Every animated property is a value timeline. It starts at the layer's
 * authored rest (or at the first block's `from`, when that block declares
 * one), and each block in start order moves it from `from` (default: the
 * timeline's current value) to `to` (default: rest) over the block's
 * duration, holding `to` afterwards. The style a layer gets at a frame is
 * whatever differs from rest across its timelines.
 */
import { interpolateColors } from "remotion";
import { clamp01, easedProgress, lerp } from "./easings";
import type {
  AnimationBlock,
  MovePoint,
  WipeOrigin,
} from "@hightouch/lib/ad-studio/html-editor/animation-blocks";
import type { LayerBounds } from "./doc-motion-geometry";

/** The CSS a set of blocks contributes to a layer at one frame. Composed
 * onto the layer's own styles by the caller — opacity multiplies, transforms
 * append, everything else overrides. */
export interface BlockStyle {
  opacity?: number;
  transform?: string;
  transformOrigin?: string;
  filter?: string;
  clipPath?: string;
  visibility?: "hidden";
  /** Resize: the layer's animated box, px. */
  width?: number;
  height?: number;
  /** Color: a CSS color; the caller decides which property it paints (text
   * color on a text layer, background elsewhere). */
  color?: string;
  /** Corner radius, px. */
  borderRadius?: number;
  /** Stroke: the drawn fraction of every shape in an SVG layer, 0 to 1. */
  strokeDrawn?: number;
}

export interface BlockEvalContext {
  fps: number;
  /** Layer geometry for `move` edge travel and circle wipes. */
  bounds?: LayerBounds;
  /** The authored values the color and corner-radius timelines rest at.
   * Without a rest color, a color block cannot interpolate and jumps to its
   * target; without a rest radius, rest is 0. */
  rest?: { color?: string; borderRadius?: number };
}

/**
 * Last resort when the layer's box is unknown: a flex group's child, whose
 * model `x`/`y` is not a position, or a caller that threaded no geometry.
 * A 120px square at the canvas origin travels 120px past every edge: enough
 * to read as motion, not the off-canvas travel the author asked for.
 */
const UNMEASURED_BOUNDS: LayerBounds = {
  rect: { x: 0, y: 0, width: 120, height: 120 },
  canvas: { width: 120, height: 120 },
};

/** Travel that puts the layer fully past the given canvas edge. */
function edgeTravel(
  edge: "top" | "bottom" | "left" | "right",
  bounds: LayerBounds | undefined,
): { x: number; y: number } {
  const { rect, canvas } = bounds ?? UNMEASURED_BOUNDS;
  switch (edge) {
    case "top":
      return { x: 0, y: -(rect.y + rect.height) };
    case "bottom":
      return { x: 0, y: canvas.height - rect.y };
    case "left":
      return { x: -(rect.x + rect.width), y: 0 };
    case "right":
      return { x: canvas.width - rect.x, y: 0 };
  }
}

/**
 * Eased progress for the block at `frame` (frames relative to layer start,
 * matching `block.start`). Held at 0 before the block and 1 after it.
 */
export function blockProgress(block: AnimationBlock, frame: number): number {
  const local = frame - block.start;
  if (local < 0) return 0;
  if (block.duration <= 0 || local >= block.duration) return 1;
  return easedProgress(block.easing, local / block.duration);
}

const byStart = <B extends AnimationBlock>(blocks: readonly B[]): B[] =>
  [...blocks].sort((a, b) => a.start - b.start);

/**
 * One property's value at `frame`. `ends` reads a block's `from`/`to` on
 * this property; either may be undefined. A block's implicit `from` is the
 * timeline's value when it runs, its implicit `to` is `rest`; the first
 * block's explicit `from`, when present, is also the value before it starts.
 */
function timelineValue<B extends AnimationBlock>(
  blocks: readonly B[],
  frame: number,
  rest: number,
  ends: (block: B) => {
    from?: number;
    to?: number;
  },
): number {
  return sampleTimeline(blocks, frame, rest, ends, lerp);
}

function sampleTimeline<B extends AnimationBlock, T>(
  blocks: readonly B[],
  frame: number,
  rest: T,
  ends: (block: B) => { from?: T; to?: T },
  interpolate: (from: T, to: T, progress: number) => T,
): T {
  const sorted = byStart(blocks);
  const first = sorted[0];
  const initial = first ? (ends(first).from ?? rest) : rest;
  const resolved: { block: B; from: T; to: T }[] = [];
  const at = (time: number): T => {
    for (let i = resolved.length - 1; i >= 0; i--) {
      const entry = resolved[i];
      if (entry && time >= entry.block.start) {
        return interpolate(
          entry.from,
          entry.to,
          blockProgress(entry.block, time),
        );
      }
    }
    return initial;
  };
  for (const block of sorted) {
    if (frame < block.start) break;
    const { from, to } = ends(block);
    // Resolve the inherited value at the handoff, before adding this block.
    resolved.push({ block, from: from ?? at(block.start), to: to ?? rest });
  }
  return at(frame);
}

/**
 * The last param of a kind across the blocks that have started; before any
 * block starts, the first block's param (the pre-start hold).
 */
function latest<B extends AnimationBlock, T>(
  blocks: readonly B[],
  frame: number,
  read: (block: B) => T | undefined,
): T | undefined {
  const sorted = byStart(blocks);
  let found: T | undefined;
  for (const block of sorted) {
    if (frame < block.start) break;
    found = read(block) ?? found;
  }
  return found ?? (sorted.length ? read(sorted[0]!) : undefined);
}

// --- Per-action channels ---

type Move = Extract<AnimationBlock, { action: "move" }>;
type Scale = Extract<AnimationBlock, { action: "scale" }>;
type Rotate = Extract<AnimationBlock, { action: "rotate" }>;
type Opacity = Extract<AnimationBlock, { action: "opacity" }>;
type Blur = Extract<AnimationBlock, { action: "blur" }>;
type Wipe = Extract<AnimationBlock, { action: "mask-wipe" }>;
type Resize = Extract<AnimationBlock, { action: "resize" }>;
type Wiggle = Extract<AnimationBlock, { action: "wiggle" }>;
type HideShow = Extract<AnimationBlock, { action: "hide-show" }>;
type Color = Extract<AnimationBlock, { action: "color" }>;
type Radius = Extract<AnimationBlock, { action: "corner-radius" }>;
type Stroke = Extract<AnimationBlock, { action: "stroke" }>;

/** One axis of a move point: the edge travel, else the px offset (0 when
 *  the axis is not given); unset when the point itself is absent. */
function axisOf(
  point: MovePoint | undefined,
  axis: "x" | "y",
  bounds: LayerBounds | undefined,
): number | undefined {
  if (!point) return undefined;
  if ("edge" in point) return edgeTravel(point.edge, bounds)[axis];
  return point[axis] ?? 0;
}

function moveStyle(
  blocks: readonly Move[],
  frame: number,
  bounds: LayerBounds | undefined,
): BlockStyle | null {
  if (!blocks.length) return null;
  const axis = (name: "x" | "y") =>
    timelineValue(blocks, frame, 0, (block) => {
      const { from, to } = block.params;
      return {
        from: axisOf(from, name, bounds),
        to: axisOf(to, name, bounds),
      };
    });
  const x = axis("x");
  const y = axis("y");
  if (x === 0 && y === 0) return null;
  return { transform: `translate(${x.toFixed(3)}px, ${y.toFixed(3)}px)` };
}

function transformStyle(
  blocks: readonly (Scale | Rotate)[],
  frame: number,
  action: "scale" | "rotate",
): BlockStyle | null {
  if (!blocks.length) return null;
  const rest = action === "scale" ? 1 : 0;
  const value = timelineValue(blocks, frame, rest, (block) => block.params);
  const origin = latest(blocks, frame, (block) => block.params.origin);
  if (value === rest) return null;
  return {
    transform:
      action === "scale"
        ? `scale(${value.toFixed(4)})`
        : `rotate(${value.toFixed(3)}deg)`,
    ...(origin ? { transformOrigin: origin } : {}),
  };
}

function opacityStyle(
  blocks: readonly Opacity[],
  frame: number,
): BlockStyle | null {
  if (!blocks.length) return null;
  // Overshooting eases would push opacity outside CSS's valid range.
  const value = clamp01(
    timelineValue(blocks, frame, 1, (block) => block.params),
  );
  return value === 1 ? null : { opacity: value };
}

function blurStyle(blocks: readonly Blur[], frame: number): BlockStyle | null {
  if (!blocks.length) return null;
  const value = timelineValue(blocks, frame, 0, (block) => block.params);
  return value <= 0 ? null : { filter: `blur(${value.toFixed(3)}px)` };
}

/** clip-path insets for a rect wipe: reveal grows from the given origin. */
function rectWipe(origin: WipeOrigin, revealed: number): string {
  const hidden = Math.max(0, (1 - revealed) * 100);
  switch (origin) {
    case "left":
      return `inset(0% ${hidden}% 0% 0%)`;
    case "right":
      return `inset(0% 0% 0% ${hidden}%)`;
    case "top":
      return `inset(0% 0% ${hidden}% 0%)`;
    case "bottom":
      return `inset(${hidden}% 0% 0% 0%)`;
    case "center": {
      const half = hidden / 2;
      return `inset(${half}% ${half}% ${half}% ${half}%)`;
    }
  }
}

function circleWipe(revealed: number): string {
  // 70.7107% (≈ √2/2) covers the corners of the box, so revealed=1 shows
  // everything regardless of aspect ratio.
  return `circle(${(Math.max(0, revealed) * 70.7107).toFixed(3)}% at 50% 50%)`;
}

function wipeStyle(blocks: readonly Wipe[], frame: number): BlockStyle | null {
  if (!blocks.length) return null;
  const revealed = timelineValue(blocks, frame, 1, (block) => block.params);
  if (revealed >= 1) return null;
  const shape = latest(blocks, frame, (block) => block.params.shape);
  const edge = latest(blocks, frame, (block) => block.params.edge) ?? "left";
  return {
    clipPath:
      shape === "circle" ? circleWipe(revealed) : rectWipe(edge, revealed),
  };
}

function strokeStyle(
  blocks: readonly Stroke[],
  frame: number,
): BlockStyle | null {
  if (!blocks.length) return null;
  const drawn = timelineValue(blocks, frame, 1, (block) => block.params);
  if (drawn >= 1) return null;
  return { strokeDrawn: Math.max(0, drawn) };
}

/**
 * Visibility is a toggle timeline: a layer whose first toggle shows it is
 * hidden until then, and each toggle at or before `frame` sets the state.
 */
function visibilityStyle(
  blocks: readonly HideShow[],
  frame: number,
): BlockStyle | null {
  if (!blocks.length) return null;
  const sorted = byStart(blocks);
  let hidden = sorted[0]!.params.visible;
  for (const block of sorted) {
    if (frame < block.start) break;
    hidden = !block.params.visible;
  }
  return hidden ? { visibility: "hidden" } : null;
}

/**
 * Resize blocks are one per-axis value timeline whose rest is the layer's
 * measured size, so resize needs `bounds` (without them it contributes
 * nothing). Resizes anchor on the element center: Jitter/Lottie rects grow
 * about their center while CSS boxes grow from the top-left, so the emitted
 * translate re-centers the box. A grow anchor already holds part of that
 * (a center-anchored text box stays centered on its own), so the translate
 * covers only the remainder. It must stay OUTSIDE the move/scale/rotate
 * timelines in the transform list (the caller orders it first): the box first
 * transforms about its own center, then the translate restores that center
 * to the authored one.
 */
function resizeStyle(
  blocks: readonly Resize[],
  frame: number,
  bounds: LayerBounds | undefined,
): BlockStyle | null {
  if (!blocks.length || !bounds) return null;
  const rest = bounds.rect;
  const width = timelineValue(blocks, frame, rest.width, (block) => ({
    to: block.params.width ?? rest.width,
  }));
  const height = timelineValue(blocks, frame, rest.height, (block) => ({
    to: block.params.height ?? rest.height,
  }));
  if (width === rest.width && height === rest.height) return null;
  const anchor = rest.anchor ?? { x: 0, y: 0 };
  const dx = (rest.width - width) * (0.5 - anchor.x);
  const dy = (rest.height - height) * (0.5 - anchor.y);
  return {
    ...(blocks.some((block) => block.params.width !== undefined)
      ? { width }
      : {}),
    ...(blocks.some((block) => block.params.height !== undefined)
      ? { height }
      : {}),
    ...(dx !== 0 || dy !== 0
      ? { transform: `translate(${dx.toFixed(3)}px, ${dy.toFixed(3)}px)` }
      : {}),
  };
}

/** A CSS color as remotion's normalized rgba string, or undefined when a
 * block cannot interpolate from it (gradients, var(), malformed input). */
function cssColor(value: string | undefined): string | undefined {
  if (!value) return undefined;
  try {
    return interpolateColors(0, [0, 1], [value, value]);
  } catch {
    return undefined;
  }
}

/**
 * Color chains like a numeric timeline, with remotion's `interpolateColors`
 * as the lerp. The rest color comes from the authored layer; when it is
 * unknown, or a block names a color the parser cannot read, that end is the
 * block's own `to`, which makes the block a hard switch rather than a fade.
 */
function colorStyle(
  blocks: readonly Color[],
  frame: number,
  restColor: string | undefined,
): BlockStyle | null {
  if (!blocks.length) return null;
  const rest = cssColor(restColor);
  const value = sampleTimeline<Color, string | undefined>(
    blocks,
    frame,
    rest,
    (block) => ({
      from: cssColor(block.params.from),
      to: cssColor(block.params.to),
    }),
    (from, to, progress) =>
      to === undefined
        ? from
        : interpolateColors(progress, [0, 1], [from ?? to, to]),
  );
  return value === undefined || value === rest ? null : { color: value };
}

function radiusStyle(
  blocks: readonly Radius[],
  frame: number,
  restRadius = 0,
): BlockStyle | null {
  if (!blocks.length) return null;
  const value = timelineValue(
    blocks,
    frame,
    restRadius,
    (block) => block.params,
  );
  return value === restRadius ? null : { borderRadius: Math.max(0, value) };
}

/**
 * Oscillates on raw local time for the block's duration; never eased. The
 * amplitude ramps in over the first period and out over the last, so the
 * motion starts from rest and settles back to it at any duration.
 */
function wiggleStyle(
  block: Wiggle,
  frame: number,
  fps: number,
): BlockStyle | null {
  const local = frame - block.start;
  if (local < 0 || local >= block.duration) return null;
  const speed = block.params.speed ?? 0.4;
  const phase = (local / fps) * speed * Math.PI * 2;
  const ramp = Math.min(fps / speed, block.duration / 2);
  const envelope = Math.min(1, local / ramp, (block.duration - local) / ramp);
  if (block.params.property === "scale") {
    // Breathes between 1 and 1 + distance, matching the legacy pulse shape.
    const amplitude = (block.params.distance ?? 0.03) * envelope;
    const value = 1 + ((Math.sin(phase) + 1) / 2) * amplitude;
    return { transform: `scale(${value.toFixed(4)})` };
  }
  const distance = (block.params.distance ?? 6) * envelope;
  const axis = block.params.axis ?? "y";
  const dx = axis === "x" || axis === "both" ? Math.cos(phase) * distance : 0;
  const dy = axis === "y" || axis === "both" ? Math.sin(phase) * distance : 0;
  return { transform: `translate(${dx.toFixed(3)}px, ${dy.toFixed(3)}px)` };
}

/**
 * Compose per-channel styles into one: opacity multiplies; transforms append
 * in list order, so CSS applies later entries first and earlier entries wrap
 * outside them; filters chain; clip-path/visibility/origin take the last
 * contributor. Null entries (identity) are skipped.
 */
function composeBlockStyles(
  styles: readonly (BlockStyle | null)[],
): BlockStyle | null {
  const out: BlockStyle = {};
  const transforms: string[] = [];
  const filters: string[] = [];
  for (const style of styles) {
    if (!style) continue;
    const { opacity, transform, filter, ...rest } = style;
    if (opacity !== undefined) out.opacity = (out.opacity ?? 1) * opacity;
    if (transform) transforms.push(transform);
    if (filter) filters.push(filter);
    Object.assign(out, rest);
  }
  if (transforms.length) out.transform = transforms.join(" ");
  if (filters.length) out.filter = filters.join(" ");
  return Object.keys(out).length ? out : null;
}

/** Combine every block on a layer into the CSS for one frame. */
export function blockStylesForLayer(
  blocks: readonly AnimationBlock[] | undefined,
  frame: number,
  context: BlockEvalContext,
): BlockStyle | null {
  if (!blocks?.length) return null;
  const of = <A extends AnimationBlock["action"]>(action: A) =>
    blocks.filter(
      (block): block is Extract<AnimationBlock, { action: A }> =>
        block.action === action,
    );
  // Transform order is `resize move wiggle scale rotate`: translates sit
  // outside scale and rotate so travel is measured in canvas px, not shrunk
  // or turned by the layer's own transform. The resize re-centering translate
  // goes first so it stays outside every other transform.
  return composeBlockStyles([
    resizeStyle(of("resize"), frame, context.bounds),
    moveStyle(of("move"), frame, context.bounds),
    ...of("wiggle").map((block) => wiggleStyle(block, frame, context.fps)),
    transformStyle(of("scale"), frame, "scale"),
    transformStyle(of("rotate"), frame, "rotate"),
    opacityStyle(of("opacity"), frame),
    blurStyle(of("blur"), frame),
    wipeStyle(of("mask-wipe"), frame),
    visibilityStyle(of("hide-show"), frame),
    colorStyle(of("color"), frame, context.rest?.color),
    radiusStyle(of("corner-radius"), frame, context.rest?.borderRadius),
    strokeStyle(of("stroke"), frame),
  ]);
}
